import { ErrorMessage } from "@hookform/error-message";
import * as moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  UncontrolledDropdown,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Row,
  RSelect,
} from "../../components/Component";
import Table from "../../components/react-table/Table";
import { DELETE, GET, POST, PUT } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { DEFAULT_DELIVERY_PROVIDER_ID } from "../../utils/Utils";
import UpsertDeliveryFee from './UpsertDeliveryFee';

const DEFAULT_DELIVERY_FEE = {
  id: "",
  warehouseId: '',
  customerId: "",
  amount: "",
  deliveryProviderId: DEFAULT_DELIVERY_PROVIDER_ID,
  unitNetPrice: null,

};

const DeliveryFeeManager = () => {
  const [data, setData] = useState([]);
  const [view, setView] = useState({
    edit: false,
    add: false,
    details: false,
    isDelete: false,
  });

  const { errors, watch, handleSubmit, reset, getValues, control, setValue } = useForm(
    {
      defaultValues: DEFAULT_DELIVERY_FEE,
    }
  );

  const [loading, setLoading] = useState(false);
  const [editId, setEditedId] = useState();
  const [deleteId, setDeleteId] = useState();
  const [timeNow, setTimeNow] = useState();
  const [smOption, setSmOption] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  const [filter, setFilter] = useState({
    customerId: undefined,
    warehouseId: undefined,
  });

  const colTable = [
    {
      Header: "Mã",
      accessor: "id",
    },
    {
      Header: "Tên khách hàng",
      accessor: (item) => item.customer.name,
    },
    {
      Header: "Nhà vận chuyển",
      accessor: (item) => item.deliveryProvider?.name,
    },
    {
      Header: "Tên kho",
      accessor: (item) => item?.warehouse?.name,
    },
    {
      Header: "Giá(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.amount}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Giá NET(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.unitNetPrice}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Giá xuất khẩu(đ)",
      className: 'text-right',
      accessor: (item) => <NumericFormat
        value={item.amountExternal}
        displayType={'text'}
        decimalScale={2}
        thousandSeparator={','}
        thousandsGroupStyle="thousand"
      />
    },
    {
      Header: "Ngày tạo",
      accessor: (item) => moment(item.createdAt).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      Header: "Hành động",
      accessor: (item) => (
        <ul className="nk-tb-actions gx-1">
          <li>
            <UncontrolledDropdown>
              <DropdownToggle
                tag="a"
                className="btn btn-icon dropdown-toggle btn-trigger"
              >
                <Icon name="more-h"></Icon>
              </DropdownToggle>
              <DropdownMenu right>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#dropdown"
                      onClick={(ev) => {
                        ev.preventDefault();
                        onEditClick(item.id);
                        toggle("edit");
                      }}
                    >
                      <Icon name="edit" className="text-primary"></Icon>
                      <span>Sửa</span>
                    </DropdownItem>
                  </li>
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#dropdown"
                      onClick={(ev) => {
                        ev.preventDefault();
                        setDeleteId(item.id);
                        toggle("isDelete");
                      }}
                    >
                      <Icon name="trash" className="text-danger"></Icon>
                      <span>Xoá</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      ),
    },
  ];

  useEffect(() => {
    GET("/warehouse", {}, true).then((resp) => {
      setWarehouseOptions(
        resp.data?.rows?.map(
          (c) =>
          ({
            label: c.name,
            value: c.id,
            data: c,
          })
        )
      );
    });

    GET("customer", {}, true)
      .then(({ data: customer }) => {
        setCustomerOptions(
          customer?.map((c) => ({
            label: c.name,
            value: c.id,
            data: c,
          }))
        )
      });
  }, []);

  // Changing state value when searching name
  useEffect(() => {
    setLoading(true);
    setData([]);
    let params = {
      ...(filter.customerId ? { customerId: filter.customerId } : {}),
      ...(filter.warehouseId ? { warehouseId: filter.warehouseId } : {}),
      ...(filter.deliveryProviderId ? { deliveryProviderId: filter.deliveryProviderId } : {})
    };

    GET(`delivery-fee`, params, true)
      .then(({ data }) => {
        setData([...data?.rows]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filter, timeNow]);

  // toggle function to view order details
  const toggle = (type) => {
    setView({
      ...view,
      [type]: !view[type],
    });
  };

  const resetData = () => {
    setEditedId("");
    onFormCancel();
    setTimeNow(moment().valueOf());
  };

  // Submits form data
  const onHandleSubmit = () => {
    setLoading(true);
    const { amount, customerId, amountExternal, warehouseId, deliveryProviderId, unitNetPrice } = getValues();

    const formData = {
      amount,
      customerId,
      amountExternal,
      warehouseId,
      deliveryProviderId,
      unitNetPrice: unitNetPrice ?? null,
    };

    const request = editId
      ? PUT(`delivery-fee/${editId}`, formData, {}, true)
      : POST("delivery-fee", formData, {}, true);

    request
      .then((res) => {
        if (editId) {
          toast.success("Cập nhật thành công");
        } else {
          toast.success("Thêm thành công");
        }
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };


  // function to close the form modal
  const onFormCancel = () => {
    setView({ add: false, edit: false, isDelete: false });
    reset({
      ...DEFAULT_DELIVERY_FEE
    });
  };

  const deleteOrder = (id) => {
    if (!id) {
      return;
    }
    DELETE(`delivery-fee/${id}`, {}, true)
      .then((res) => {
        toast.success("Xóa giá vận chuyển thành công");
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      });
  };

  const onEditClick = (id) => {
    setLoading(true);
    setEditedId(id);

    GET(`/delivery-fee/${id}`, {
    }, true)
      .then(({ data }) => {
        reset({
          ...data,
        });
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
        onFormCancel();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <React.Fragment>
      <Head title="Quản lý giá vận chuyển"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Giá vận chuyển</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <div className="form-group">
                        <div className="form-control-wrap">
                          <RSelect
                            isClearable
                            placeholder={"Lọc theo kho"}
                            options={warehouseOptions}
                            value={warehouseOptions.find((c) => c.value === filter.warehouseId) || null}
                            onChange={(val) => {
                              setFilter({
                                ...filter,
                                warehouseId: val?.value || undefined,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </li>
                    <li>
                      <div
                        className="form-control-wrap"
                        style={{ width: "200px" }}
                      >
                        <RSelect
                          isClearable
                          placeholder="Khách hàng/Nhà cung cấp"
                          options={customerOptions}
                          value={
                            customerOptions.find(
                              (c) => c.value == filter.customerId
                            ) || null
                          }
                          onChange={(val) =>
                            setFilter({
                              ...filter,
                              customerId: val?.value || undefined,
                            })
                          }
                        />
                      </div>
                    </li>
                    <li>
                      <div
                        className="form-control-wrap"
                        style={{ width: "200px" }}
                      >
                        <RSelect
                          placeholder="Nhà vận chuyển"
                          options={customerOptions.filter(c => c.data.isDeliveryProvider)}
                          value={
                            customerOptions.filter(c => c.data.isDeliveryProvider).find(
                              (c) => c.value === filter.deliveryProviderId
                            ) || null
                          }
                          onChange={(val) =>
                            setFilter({
                              ...filter,
                              deliveryProviderId: val?.value,
                            })
                          }
                        />
                      </div>
                    </li>
                    <li>
                      <div>
                        <Button
                          className="btn-primary"
                          color="primary"
                          onClick={() => {
                            onFormCancel();
                            toggle("add");
                          }}
                        >
                          <Icon name="plus"></Icon>
                          <span>Thêm giá phí vận chuyển</span>
                        </Button>
                      </div>
                    </li>
                    <li>
                      <UpsertDeliveryFee reload={resetData} />
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div
                id="DataTables_Table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4 no-footer"
              >
                <div className="row justify-between g-2">
                  <div className="col-8 col-sm-6 text-start"></div>
                  <div className="col-5 col-sm-6 text-end">
                    <div className="datatable-filter">
                      <div className="d-flex justify-content-end g-2">
                        <div
                          className="dataTables_length"
                          id="DataTables_Table_1_length"
                        >
                          {/* <label>
                            <span className="d-none d-sm-inline-block">Show</span>
                            <Input type="month" />
                          </label> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="my-3">
                  <Table
                    loading={loading}
                    columns={colTable}
                    data={data}
                    notShowPagi={true}
                  ></Table>
                </div>
              </div>
            </div>
          </div>
        </Block>

        <Modal
          isOpen={view.add || view.edit}
          toggle={() => onFormCancel()}
          className="modal-dialog-centered"
          size="lg"
        >
          <ModalBody>
            <a href="#cancel" className="close">
              {" "}
              <Icon
                name="cross-sm"
                onClick={(ev) => {
                  ev.preventDefault();
                  onFormCancel();
                }}
              ></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Thêm giá vận chuyển</h5>
              <div className="mt-4">
                <form onSubmit={handleSubmit(onHandleSubmit)}>
                  <Row className="g-3">


                    <Col size="12">
                      <div className="form-group">
                        <label className="form-label" >
                          Khách hàng<span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="customerId"
                            rules={{ required: "Vui lòng nhập thông tin" }}
                            render={({ onChange, value, name, ref }) => (
                              <RSelect
                                inputRef={ref}
                                name={name}
                                options={customerOptions}
                                value={customerOptions.find(
                                  (c) => c.value === value
                                )}
                                onChange={(val) => onChange(val.value)}
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="customerId"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>

                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Kho
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="warehouseId"
                            rules={{ required: "Vui lòng nhập thông tin" }}

                            render={({ onChange, value, name, ref }) => (
                              <RSelect
                                isClearable
                                classNamePrefix="form-control"
                                inputRef={ref}
                                name={name}
                                options={warehouseOptions}
                                value={warehouseOptions.find(
                                  (c) => c.value === value
                                )}
                                onChange={(val) => {
                                  onChange(val?.value);
                                }}
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="warehouseId"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col size="12">
                      <div className="form-group">
                        <label className="form-label" >
                          Nhà vận chuyển
                          <span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="deliveryProviderId"
                            rules={{ required: "Vui lòng nhập thông tin" }}
                            render={({ onChange, value, name, ref }) => (
                              <RSelect
                                inputRef={ref}
                                name={name}
                                options={customerOptions.filter(c => c.data?.isDeliveryProvider)}
                                value={customerOptions.find(
                                  (c) => c.value === value
                                )}
                                onChange={(val) => onChange(val.value)}
                              />
                            )}
                          />

                        </div>
                      </div>
                    </Col>
                    <Col size="12">
                      <div className="form-group">
                        <label className="form-label" >
                          Đơn giá(đ)
                          <span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="amount"
                            rules={{ required: "Vui lòng nhập thông tin" }}
                            render={({ onChange, value, ref }) => (
                              <NumericFormat
                                className="form-control"
                                inputRef={ref}
                                type="text"
                                value={value}
                                thousandsGroupStyle="thousand"
                                thousandSeparator=","
                                decimalScale={2}
                                onValueChange={(val) =>
                                  onChange(val.floatValue)
                                }
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="amount"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col size="12">
                      <div className="form-group">
                        <label className="form-label" >
                          Đơn giá xuất khẩu(đ)
                          <span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="amountExternal"
                            rules={{ required: "Vui lòng nhập thông tin" }}
                            render={({ onChange, value, ref }) => (
                              <NumericFormat
                                className="form-control"
                                inputRef={ref}
                                type="text"
                                value={value}
                                thousandsGroupStyle="thousand"
                                thousandSeparator=","
                                decimalScale={2}
                                onValueChange={(val) =>
                                  onChange(val.floatValue)
                                }
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="amountExternal"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col size="12">
                      <div className="form-group">
                        <label className="form-label" >
                          Đơn giá NET(đ)
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="unitNetPrice"
                            render={({ onChange, value, ref }) => (
                              <NumericFormat
                                className="form-control"
                                inputRef={ref}
                                type="text"
                                value={value}
                                thousandsGroupStyle="thousand"
                                thousandSeparator=","
                                decimalScale={2}
                                onValueChange={(val) =>
                                  onChange(val.floatValue)
                                }
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="unitNetPrice"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col size="12">
                      <Button color="primary" type="submit">
                        <Icon className="plus"></Icon>
                        <span>Thêm giá vận chuyển</span>
                      </Button>
                    </Col>
                  </Row>
                </form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={view.isDelete} toggle={() => onFormCancel()}>
          <ModalBody className="modal-body-lg text-center">
            <div className="nk-modal">
              <Icon className="nk-modal-icon icon-circle icon-circle-xxl ni ni-cross bg-danger"></Icon>
              <h4 className="nk-modal-title">
                Bạn có chắc muốn xoá giá vận chuyển này không ?{" "}
              </h4>
              <div className="nk-modal-action mt-5 d-flex justify-content-around">
                <Button
                  color="light"
                  size="lg"
                  className="btn-mw mr-5"
                  onClick={() => onFormCancel()}
                >
                  Huỷ bỏ
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  className="btn-mw"
                  onClick={(ev) => {
                    ev.preventDefault();
                    deleteOrder(deleteId);
                  }}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default DeliveryFeeManager;
